import React, { useState } from "react";
import { Card, Radio, Space, Button, Modal, Alert, Table, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { switchPricingModel } from "../../store/slice/pricing";
import { generatePriceMatrix } from "./PricingCalculatorPreview";

const PRICING_MODELS = [
  {
    id: "baseMultiplier",
    label: "Model 1: Base Fee + Multiplier",
    description: "Simple and flexible. Set a base fee per locality, then apply demand tier multipliers.",
    icon: "📊",
    bestFor: "Most use cases with consistent demand patterns",
    useCases: [
      "Quick setup with 4 base values",
      "Auto-calculated for all demand tiers",
      "Easy to adjust margins",
    ],
  },
  {
    id: "directValues",
    label: "Model 2: Direct Values Matrix",
    description: "Full control. Set exact prices for every locality × demand tier combination (4×3 grid).",
    icon: "🎯",
    bestFor: "Complex pricing with fine-grained control",
    useCases: [
      "No multipliers applied",
      "Complete independence per combo",
      "Best for market-specific pricing",
    ],
  },
  {
    id: "hybrid",
    label: "Model 3: Hybrid (Base + Overrides)",
    description: "Flexible hybrid. Use base fees with multipliers, but override specific combos as needed.",
    icon: "⚙️",
    bestFor: "Base model with selective adjustments",
    useCases: [
      "Start from base fees",
      "Override only where needed",
      "Audit trail of overrides",
    ],
  },
];

function PricingModelSelector() {
  const dispatch = useDispatch();
  const {
    currentPricingModel,
    baseFeesModel,
    demandMultipliers,
    directValues,
    overrides,
    modelSwitchInProgress,
  } = useSelector((state) => state.pricing);

  const [selectedModel, setSelectedModel] = useState(currentPricingModel);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleModelChange = (newModel) => {
    if (newModel === currentPricingModel) {
      setSelectedModel(newModel);
      return;
    }

    // Show confirmation dialog
    setSelectedModel(newModel);

    // Generate migration preview if switching TO directValues FROM baseMultiplier
    if (newModel === "directValues" && currentPricingModel === "baseMultiplier") {
      const preview = {};
      ["joiningFee", "proMonthly", "premiumMonthly", "featuredWeekly"].forEach(
        (priceType) => {
          preview[priceType] = generatePriceMatrix(
            "baseMultiplier",
            priceType,
            baseFeesModel,
            demandMultipliers,
            directValues,
            overrides
          );
        }
      );
      setMigrationPreview(preview);
    } else {
      setMigrationPreview(null);
    }

    setShowConfirmation(true);
  };

  const handleConfirmSwitch = async () => {
    setIsProcessing(true);
    try {
      const resultAction = await dispatch(switchPricingModel({ newModel: selectedModel }));
      if (switchPricingModel.fulfilled.match(resultAction)) {
        setShowConfirmation(false);
        setMigrationPreview(null);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setSelectedModel(currentPricingModel);
    setShowConfirmation(false);
    setMigrationPreview(null);
  };

  return (
    <Card className="mb-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4">Pricing Model Selection</h3>
        <Alert
          type="info"
          showIcon
          message="Choose a pricing model that best suits your business needs"
          className="mb-6"
        />
      </div>

      <Space direction="vertical" className="w-full" size="large">
        <Radio.Group
          value={currentPricingModel}
          onChange={(e) => handleModelChange(e.target.value)}
          className="w-full"
        >
          {PRICING_MODELS.map((model) => (
            <div
              key={model.id}
              className={`mb-4 p-4 rounded border-2 cursor-pointer transition ${
                currentPricingModel === model.id
                  ? "border-orange-500 bg-orange-50"
                  : "border-gray-200 hover:border-orange-300 bg-white"
              }`}
            >
              <div className="flex items-start gap-4">
                <Radio value={model.id} className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">{model.icon}</span>
                    <div>
                      <h4 className="font-semibold text-base">{model.label}</h4>
                      <p className="text-sm text-gray-600">{model.description}</p>
                    </div>
                  </div>

                  <div className="mt-2 ml-10">
                    <p className="text-xs font-medium text-gray-700 mb-1">
                      Best for: {model.bestFor}
                    </p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {model.useCases.map((useCase, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className="text-green-600">✓</span>
                          {useCase}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </Radio.Group>
      </Space>

      {/* Confirmation Modal */}
      <Modal
        title="Switch Pricing Model"
        open={showConfirmation}
        onOk={handleConfirmSwitch}
        onCancel={handleCancel}
        okText="Confirm Switch"
        cancelText="Cancel"
        confirmLoading={isProcessing || modelSwitchInProgress}
        width={800}
      >
        <div className="space-y-4">
          <Alert
            type="warning"
            showIcon
            message="You are about to switch pricing models"
            description="This action will change how pricing is calculated. Please review the migration preview below."
          />

          <div>
            <p className="font-semibold mb-2">
              Switching from <span className="text-blue-600">{currentPricingModel}</span> to{" "}
              <span className="text-orange-600">{selectedModel}</span>
            </p>

            {migrationPreview && currentPricingModel === "baseMultiplier" && selectedModel === "directValues" && (
              <div>
                <p className="text-sm text-gray-700 mb-3">
                  Based on your current base fees and multipliers, the following direct values will be auto-generated:
                </p>
                <MigrationPreviewTables preview={migrationPreview} />
              </div>
            )}

            {selectedModel === "hybrid" && currentPricingModel !== "hybrid" && (
              <Alert
                type="info"
                showIcon
                message="Hybrid Model Info"
                description="You can keep your current base fees and multipliers, then add price overrides for specific locality × demand tier combinations."
                className="mt-4"
              />
            )}
          </div>
        </div>
      </Modal>
    </Card>
  );
}

function MigrationPreviewTables({ preview }) {
  const priceTypes = ["joiningFee", "proMonthly", "premiumMonthly", "featuredWeekly"];
  const priceTypeLabels = {
    joiningFee: "Joining Fee",
    proMonthly: "Pro Monthly",
    premiumMonthly: "Premium Monthly",
    featuredWeekly: "Featured Weekly",
  };

  return (
    <div className="space-y-4">
      {priceTypes.map((priceType) => (
        <div key={priceType}>
          <h5 className="font-medium text-sm mb-2">{priceTypeLabels[priceType]}</h5>
          <table className="w-full border-collapse text-xs bg-white">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-2 py-1">Locality</th>
                <th className="border border-gray-300 px-2 py-1">High</th>
                <th className="border border-gray-300 px-2 py-1">Standard</th>
                <th className="border border-gray-300 px-2 py-1">Low</th>
              </tr>
            </thead>
            <tbody>
              {["metro", "urban", "suburban", "rural"].map((locality) => (
                <tr key={locality}>
                  <td className="border border-gray-300 px-2 py-1 capitalize font-medium">
                    {locality}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-center">
                    ₹{preview[priceType][locality].high}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-center">
                    ₹{preview[priceType][locality].standard}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-center">
                    ₹{preview[priceType][locality].low}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export default PricingModelSelector;
